import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  CustomExperimentSpec,
  PanelRow,
  ActionBarSpec,
} from '@learnforge/demo-render/harness';
import { createReadout, type ReadoutHandle, type ReadoutRow } from '@learnforge/demo-render';
import { createSvgEl } from '@learnforge/demo-render';
import { niceCeil } from '@learnforge/demo-math/numeric';
import type { FinanceContext } from '../types';
import type { FinanceKey } from '../locales/keys';
import { createChart, type ChartHandle } from '../render/chart';
import { createLineSeries, type LineSeriesHandle } from '../render/line-series';
import { createAreaStack, type AreaStackHandle, type Band } from '../render/area-stack';
import { createCoinIcon } from '../render/coin';
import { feeAdjustedSeries } from '../finance/fees';
import { type Series } from '../finance/series';
import { formatMoney, formatPercent, formatYears } from '../finance/format';

// ── State ─────────────────────────────────────────────────────────────────────

export interface FeesDragState {
  principal: number;
  rate: number;
  fee: number;
  years: number;
  monthly: number; // optional monthly contribution
}

// ── Pure model (unit-tested) ──────────────────────────────────────────────────

export function feesDragModel(state: FeesDragState): {
  series: Series[];
  gapBand?: Band;
  readout: ReadoutRow[];
} {
  const { principal, rate, fee, years, monthly } = state;

  // Build no-fee and with-fee series using feeAdjustedSeries
  const noFeeRaw = feeAdjustedSeries(principal, rate, 0, 12, years, { contribution: monthly });
  const withFeeRaw = feeAdjustedSeries(principal, rate, fee, 12, years, { contribution: monthly });

  // Relabel ids
  const noFee: Series = { ...noFeeRaw, id: 'no-fee' };
  const withFee: Series = { ...withFeeRaw, id: 'with-fee' };

  const noFeeFinal = noFee.points.at(-1)?.value ?? principal;
  const withFeeFinal = withFee.points.at(-1)?.value ?? principal;
  const feeCost = noFeeFinal - withFeeFinal;

  // Gap band: the region between withFee (lower) and noFee (upper).
  // Use approach (a): pass two bands to createAreaStack —
  //   band 1: base points = withFee values (transparent/invisible fill = just the base)
  //   band 2: gap points = (noFee - withFee) per year (visible gap fill)
  // The stacking puts band 2 exactly between withFee and noFee.
  const gapBand: Band = {
    id: 'fee-gap',
    points: noFee.points.map((p, i) => ({
      t: p.t,
      value: p.value - (withFee.points[i]?.value ?? p.value),
    })),
    className: 'fin-area-fee-gap',
  };

  const readout: ReadoutRow[] = [
    {
      id: 'final-no-fee',
      lhs: 'stat.final-no-fee.label',
      relation: '=',
      rhs: formatMoney(noFeeFinal),
    },
    {
      id: 'final-with-fee',
      lhs: 'stat.final-with-fee.label',
      relation: '=',
      rhs: formatMoney(withFeeFinal),
    },
    {
      id: 'fee-cost',
      lhs: 'stat.fee-cost.label',
      relation: '=',
      rhs: formatMoney(feeCost),
      emphasis: feeCost > 0 ? 'broken' : 'neutral',
    },
  ];

  // Advanced: fee cost as % of no-fee pot + multi-fee comparison
  if (noFeeFinal > 0) {
    const feeCostPct = feeCost / noFeeFinal;
    readout.push({
      id: 'fee-cost-pct',
      lhs: 'stat.fee-cost-pct.label',
      relation: '=',
      rhs: formatPercent(feeCostPct, 1),
    });
  }

  // Multi-fee comparison rows (0.2%, 1%, 2%) — lhs values are stat.* locale keys
  const compFees: Array<{ f: number; label: string }> = [
    { f: 0.002, label: 'stat.fee-comp-low.label' },
    { f: 0.01, label: 'stat.fee-comp-typical.label' },
    { f: 0.02, label: 'stat.fee-comp-high.label' },
  ];
  for (const { f: compFee, label: compLabel } of compFees) {
    const compSeries = feeAdjustedSeries(principal, rate, compFee, 12, years, {
      contribution: monthly,
    });
    const compFinal = compSeries.points.at(-1)?.value ?? principal;
    const compCost = noFeeFinal - compFinal;
    readout.push({
      id: `fee-comp-${Math.round(compFee * 1000)}`,
      lhs: compLabel,
      relation: '=',
      rhs: formatMoney(compCost),
    });
  }

  return { series: [noFee, withFee], gapBand, readout };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  width: 800,
  height: 460,
  padding: { top: 24, right: 32, bottom: 44, left: 80 },
  yTickCount: 5,
  xTickCount: 6,
} as const;

function defaultState(_advanced: boolean): FeesDragState {
  return {
    principal: 10000,
    rate: 0.07,
    fee: 0.01,
    years: 30,
    monthly: 0,
  };
}

const PRESETS = [
  { id: 'low-fee', labelKey: 'preset.low-fee.label' as FinanceKey },
  { id: 'typical-fee', labelKey: 'preset.typical-fee.label' as FinanceKey },
  { id: 'high-fee', labelKey: 'preset.high-fee.label' as FinanceKey },
];

function applyPresetLocal(state: FeesDragState, id: string): FeesDragState {
  switch (id) {
    case 'low-fee':
      return { ...state, principal: 10000, rate: 0.07, fee: 0.002, years: 30, monthly: 0 };
    case 'typical-fee':
      return { ...state, principal: 10000, rate: 0.07, fee: 0.01, years: 30, monthly: 0 };
    case 'high-fee':
      return { ...state, principal: 10000, rate: 0.07, fee: 0.02, years: 30, monthly: 0 };
    default:
      return state;
  }
}

function randomise(state: FeesDragState, prng: (max?: number) => number): FeesDragState {
  const principals = [5000, 10000, 20000, 50000];
  const rates = [0.05, 0.07, 0.08, 0.1];
  const fees = [0.002, 0.005, 0.01, 0.015, 0.02];
  const yearOptions = [10, 20, 30, 40];
  return {
    ...state,
    principal: principals[Math.floor(prng() * principals.length)] ?? 10000,
    rate: rates[Math.floor(prng() * rates.length)] ?? 0.07,
    fee: fees[Math.floor(prng() * fees.length)] ?? 0.01,
    years: yearOptions[Math.floor(prng() * yearOptions.length)] ?? 30,
  };
}

// ── Spec factory ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<FeesDragState, FinanceKey, FinanceContext> {
  return {
    labelKey: 'experiment.fees-drag.label',
    demoId: 'finance',
    advancedKey: 'fees-drag',

    initialState: (ctx) => defaultState(ctx.advanced),

    applyPreset: (state, id) => applyPresetLocal(state, id),

    panel(state, ctx): PanelRow<FinanceKey, FeesDragState>[] {
      const rows: PanelRow<FinanceKey, FeesDragState>[] = [
        {
          kind: 'slider',
          id: 'principal',
          labelKey: 'param.principal.label',
          min: 1000,
          max: 100000,
          step: 1000,
          value: state.principal,
          format: formatMoney,
          onChange: (v) => ({ ...state, principal: v }),
        },
        {
          kind: 'slider',
          id: 'rate',
          labelKey: 'param.rate.label',
          min: 0.01,
          max: 0.2,
          step: 0.005,
          value: state.rate,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, rate: v }),
        },
        {
          kind: 'slider',
          id: 'fee',
          labelKey: 'param.fee.label',
          min: 0,
          max: 0.03,
          step: 0.001,
          value: state.fee,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, fee: v }),
        },
        {
          kind: 'slider',
          id: 'years',
          labelKey: 'param.years.label',
          min: 1,
          max: 50,
          step: 1,
          value: state.years,
          format: formatYears,
          onChange: (v) => ({ ...state, years: v }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'panel.advanced',
        },
      ];

      if (ctx.advanced) {
        rows.splice(4, 0, {
          kind: 'slider',
          id: 'monthly',
          labelKey: 'param.monthly.label',
          min: 0,
          max: 2000,
          step: 50,
          value: state.monthly,
          format: formatMoney,
          onChange: (v) => ({ ...state, monthly: v }),
        });
      }

      return rows;
    },

    actions(_state, ctx): ActionBarSpec<FinanceKey, FeesDragState> {
      return {
        onReset: () => defaultState(ctx.advanced),
        onRandomise: (s) => randomise(s, ctx.prng),
        presets: PRESETS,
        onPreset: (s, id) => applyPresetLocal(s, id),
      };
    },

    applyShotState(state): FeesDragState {
      // Multi-decade pose with a visibly widening gap
      return {
        ...state,
        principal: 10000,
        rate: 0.07,
        fee: 0.01,
        years: 40,
        monthly: 0,
      };
    },

    createRender(ctx, host, _controls) {
      // Build chart
      const chart: ChartHandle = createChart(host, {
        ...CHART_OPTS,
        formatX: (v) => formatYears(v),
        formatY: (v) => formatMoney(v),
      });

      // Coin icon at y-axis head
      const coinGroup = createCoinIcon(18);
      coinGroup.setAttribute(
        'transform',
        `translate(${CHART_OPTS.padding.left - 4}, ${CHART_OPTS.padding.top})`,
      );
      chart.svg.appendChild(coinGroup);

      // Area stack for the gap region (between withFee and noFee)
      const areaStack: AreaStackHandle = createAreaStack(chart);

      // Line series layer (drawn above the area)
      const lines: LineSeriesHandle = createLineSeries(chart);

      // Legend group (right-anchored to avoid clipping, per inflation legend fix)
      const legendGroup = createSvgEl('g', { class: 'fin-legend' });
      chart.svg.appendChild(legendGroup);

      // Readout — lives OUTSIDE ctx.root per the harness contract
      const readout: ReadoutHandle = createReadout(document.getElementById('readout') ?? host);

      function updateLegend(): void {
        while (legendGroup.firstChild) {
          legendGroup.removeChild(legendGroup.firstChild);
        }
        const { width, padding } = CHART_OPTS;
        const labelRightX = width - padding.right - 8;
        const swatchGap = 20;
        const swatchLen = 16;
        const legendY = padding.top + 16;

        const legendItems = [
          { id: 'no-fee', label: ctx.i18n.t('series.no-fee.label'), cls: 'fin-line-no-fee' },
          {
            id: 'with-fee',
            label: ctx.i18n.t('series.with-fee.label'),
            cls: 'fin-line-with-fee',
          },
          { id: 'fee-gap', label: ctx.i18n.t('series.fee-gap.label'), cls: 'fin-area-fee-gap' },
        ];

        legendItems.forEach((item, i) => {
          const rowY = legendY + i * 18;
          const swatchX2 = labelRightX - swatchGap;
          const swatchX1 = swatchX2 - swatchLen;

          if (item.id === 'fee-gap') {
            // Draw a small rect swatch for the area
            const rect = createSvgEl('rect', {
              class: item.cls,
              x: swatchX1,
              y: rowY - 5,
              width: swatchLen,
              height: 10,
              rx: 2,
            });
            legendGroup.appendChild(rect);
          } else {
            const lineEl = createSvgEl('line', {
              class: `${item.cls} fin-legend-dot`,
              x1: swatchX1,
              y1: rowY,
              x2: swatchX2,
              y2: rowY,
            });
            legendGroup.appendChild(lineEl);
          }

          const label = createSvgEl('text', {
            class: 'fin-legend',
            x: labelRightX,
            y: rowY,
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          label.textContent = item.label;
          legendGroup.appendChild(label);
        });
      }

      return {
        update(state: FeesDragState): void {
          const m = feesDragModel(state);

          const noFee = m.series.find((s) => s.id === 'no-fee');
          const noFeeFinal = noFee?.points.at(-1)?.value ?? state.principal;

          chart.setScales({
            xDomain: [0, state.years],
            yDomain: [0, niceCeil(noFeeFinal * 1.05)],
          });

          // Draw the gap area: base band (withFee values, transparent) + gap band (noFee - withFee)
          const withFee = m.series.find((s) => s.id === 'with-fee');
          if (m.gapBand && withFee) {
            const baseBand: Band = {
              id: 'fee-base',
              points: withFee.points,
              className: 'fin-area-fee-base',
            };
            areaStack.setBands([baseBand, m.gapBand]);
          } else {
            areaStack.clear();
          }

          // Draw line series (on top of area)
          for (const s of m.series) {
            const cls = s.id === 'no-fee' ? 'fin-line-no-fee' : 'fin-line-with-fee';
            lines.setSeries(s.id, s.points, cls);
          }

          updateLegend();

          // Foundation: first 3 rows (final-no-fee, final-with-fee, fee-cost)
          // Advanced: all rows including fee-cost-pct and multi-fee comparison
          const displayRows = ctx.advanced ? m.readout : m.readout.slice(0, 3);

          readout.update(displayRows.map((r) => ({ ...r, lhs: ctx.i18n.t(r.lhs as FinanceKey) })));
        },

        dispose(): void {
          readout.destroy();
          areaStack.destroy();
          lines.destroy();
          chart.destroy();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
